// ============================================================================
// DRAWER STACK DEBUGGING UTILITIES
// ============================================================================

import { DrawerContext, DrawerItem } from './types';

export interface DebugInfo {
  timestamp: number;
  context: DrawerContext;
  stackTrace: string;
  error?: Error;
}

export class DrawerDebugger {
  private static debugLog: DebugInfo[] = [];
  private static isEnabled = false; // Disabled to prevent infinite render logging

  static log(context: DrawerContext, message: string, data?: any) {
    if (!this.isEnabled) return;

    const debugInfo: DebugInfo = {
      timestamp: Date.now(),
      context,
      stackTrace: new Error().stack || 'No stack trace available'
    };

    this.debugLog.push(debugInfo);

    console.log(`[DrawerDebugger] ${message}`, {
      context,
      data,
      timestamp: new Date(debugInfo.timestamp).toISOString()
    });
  }

  static error(context: DrawerContext, error: Error, data?: any) {
    if (!this.isEnabled) return;

    const debugInfo: DebugInfo = {
      timestamp: Date.now(),
      context,
      stackTrace: error.stack || 'No stack trace available',
      error
    };

    this.debugLog.push(debugInfo);

    console.error(`[DrawerDebugger] ERROR:`, {
      error: error.message,
      stack: error.stack,
      context,
      data,
      timestamp: new Date(debugInfo.timestamp).toISOString()
    });
  }

  static validateContext(context: DrawerContext): string[] {
    const issues: string[] = [];

    if (!context) {
      issues.push('Context is null or undefined');
      return issues;
    }

    if (typeof context.isMobile !== 'boolean') {
      issues.push('isMobile must be a boolean');
    }

    if (typeof context.isTouchDevice !== 'boolean') {
      issues.push('isTouchDevice must be a boolean');
    }

    if (!['simple', 'medium', 'complex'].includes(context.contentComplexity)) {
      issues.push(`Invalid contentComplexity: ${context.contentComplexity}`);
    }

    if (!['form', 'list', 'details', 'actions', 'navigation'].includes(context.contentType)) {
      issues.push(`Invalid contentType: ${context.contentType}`);
    }

    if (!['xs', 'sm', 'md', 'lg', 'xl'].includes(context.screenSize)) {
      issues.push(`Invalid screenSize: ${context.screenSize}`);
    }

    if (context.parentDrawer) {
      if (!context.parentDrawer.type) {
        issues.push('parentDrawer.type is missing');
      }
      if (!['modal', 'sheet', 'bottom-drawer', 'side-drawer', 'fullscreen'].includes(context.parentDrawer.type)) {
        issues.push(`Invalid parentDrawer.type: ${context.parentDrawer.type}`);
      }
    }

    if (context.userPreference && !['drawer', 'modal', 'auto'].includes(context.userPreference)) {
      issues.push(`Invalid userPreference: ${context.userPreference}`);
    }

    return issues;
  }

  static getLogs(): DebugInfo[] {
    return [...this.debugLog];
  }

  static clearLogs() {
    this.debugLog = [];
  }

  static enable() {
    this.isEnabled = true;
  }

  static disable() {
    this.isEnabled = false;
  }
}

// Helper function to safely access nested object properties
export function safeGet<T>(obj: any, path: string, defaultValue: T): T {
  try {
    const keys = path.split('.');
    let current = obj;
    
    for (const key of keys) {
      if (current === null || current === undefined) {
        return defaultValue;
      }
      current = current[key];
    }
    
    return current !== undefined ? current : defaultValue;
  } catch (error) {
    console.warn(`[DrawerDebugger] Error accessing path "${path}":`, error);
    return defaultValue;
  }
}

// Helper function to safely call Object.keys
export function safeObjectKeys(obj: any): string[] {
  try {
    if (obj === null || obj === undefined) {
      console.warn('[DrawerDebugger] Object.keys called on null/undefined:', obj);
      return [];
    }
    
    if (typeof obj !== 'object') {
      console.warn('[DrawerDebugger] Object.keys called on non-object:', typeof obj, obj);
      return [];
    }
    
    return Object.keys(obj);
  } catch (error) {
    console.error('[DrawerDebugger] Error calling Object.keys:', error, { obj });
    return [];
  }
}
